import { Maybe } from '@codegrade/maybe';
import { array } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state for the module. */
function makeInitialState() {
    return {
        ltiProviders: { pagination: { tag: 'not-started' } },
        byTenant: {},
        singleProviders: {},
        singleProviderLoaders: {},
    };
}
const moduleBuilder = storeBuilder.module('lti-providers', makeInitialState());
/** The store for LTI providers. */
export var LTIProvidersStore;
(function (LTIProvidersStore) {
    /** Resets the LTI providers state to its initial values. */
    LTIProvidersStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    /** Commits a paginated list of all LTI providers to the state. */
    const commitLtiProviders = moduleBuilder.commit((state, { data, }) => {
        // Data is assumed to be sorted by the API.
        utils.vueSet(state, 'ltiProviders', data);
    }, 'commitLtiProviders');
    /** Commits a paginated list of LTI providers for a specific tenant to the state. */
    const commitForTenant = moduleBuilder.commit((state, { tenantId, data, }) => {
        // Data is assumed to be sorted by the API.
        utils.vueSet(state.byTenant, tenantId, data);
    }, 'commitForTenant');
    /** Commits a single, fully detailed LTI provider to the state and updates all related caches. */
    const commitSingleProvider = moduleBuilder.commit((state, { provider }) => {
        // Set the provider in the dedicated cache for single items.
        utils.vueSet(state.singleProviders, provider.id, provider);
        // After committing a single item, its loader is no longer needed.
        utils.vueDelete(state.singleProviderLoaders, provider.id);
        // Also update the provider in the main paginated list to ensure data consistency.
        const existing = state.ltiProviders;
        if (existing.data != null) {
            commitLtiProviders({
                data: {
                    ...existing,
                    data: existing.data.map(old => (old.id === provider.id ? provider : old)),
                },
            });
        }
    }, 'commitSingleProvider');
    /** Commits a loader promise for a single LTI provider to the state. */
    const commitSingleProviderLoader = moduleBuilder.commit((state, { providerId, loader, }) => {
        utils.vueSetOrDelete(state.singleProviderLoaders, providerId, loader);
    }, 'commitSingleProviderLoader');
    /** Gets the paginated list of all LTI providers. */
    LTIProvidersStore.getAll = moduleBuilder.read(
    /**
     * The actual getter function.
     *
     * @param state The module's state.
     * @returns A paginated state object containing the providers.
     */
    state => utils.getPaginatedState(state.ltiProviders), 'getAll');
    /** Gets the paginated list of LTI providers for a given tenant. */
    LTIProvidersStore.getForTenant = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param tenantId The ID of the tenant.
     * @returns A paginated state object containing the providers for the tenant.
     */
    (tenantId) => utils.getPaginatedState(state.byTenant[tenantId]), 'getForTenant');
    /**
     * Gets a single LTI provider by its ID.
     *
     * It first checks for a fully loaded provider in the dedicated cache, falling
     * back to searching the main paginated list.
     */
    LTIProvidersStore.get = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param ltiProviderId The ID of the LTI provider to find.
     * @returns A `Maybe` containing the provider if found, otherwise empty.
     */
    (ltiProviderId) => Maybe.fromNullable(state.singleProviders[ltiProviderId]).altLazy(() => array.findMaybe(p => p.id === ltiProviderId, state.ltiProviders.data ?? [])), 'get');
    /** Fetches and stores a paginated list of all LTI providers. */
    LTIProvidersStore.loadAll = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitLtiProviders({ data }),
            getData: () => state.ltiProviders,
            load: async (nextToken) => client.p.lti.getAll({ nextToken }),
            toLoad,
        });
    }, 'loadAll');
    /** Fetches and stores a paginated list of LTI providers for a specific tenant. */
    LTIProvidersStore.loadForTenant = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { tenantId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitForTenant({ tenantId, data }),
            getData: () => state.byTenant[tenantId],
            load: async (nextToken) => client.p.tenant.getLtiProviders({ tenantId, nextToken }),
            toLoad,
        });
    }, 'loadForTenant');
    /**
     * Fetches and stores the full details of a single LTI provider.
     *
     * The `force` flag can be used to refetch the provider even if it already exists.
     */
    LTIProvidersStore.loadSingle = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { ltiProviderId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitSingleProviderLoader({
                providerId: ltiProviderId,
                loader: Maybe.of(loader),
            }),
            force,
            load: async () => {
                const { body: provider } = await client.p.lti.get({ ltiProviderId });
                commitSingleProvider({ provider });
            },
            loader: Maybe.fromNullable(state.singleProviderLoaders[ltiProviderId]),
            removeLoader: () => commitSingleProviderLoader({
                providerId: ltiProviderId,
                loader: Maybe.empty(),
            }),
            value: Maybe.fromNullable(state.singleProviders[ltiProviderId]),
        });
    }, 'loadSingle');
    /** Creates a new LTI provider and adds it to the local state on success. */
    LTIProvidersStore.createProvider = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves with the API response.
     */
    async ({ state }, { data, }) => {
        const res = await client.p.lti.create({ requestBody: data });
        return res.addCallbacks({
            onSuccess: () => {
                const existing = state.ltiProviders;
                commitSingleProvider({ provider: res.cgResult });
                // Only update the local state if all providers have already been loaded,
                // otherwise we would be appending to an incomplete list.
                if (existing.pagination.tag === 'finished') {
                    commitLtiProviders({
                        data: {
                            ...existing,
                            data: [...(existing.data ?? []), res.cgResult],
                        },
                    });
                }
                // Also update the tenant-specific list, if it exists and is fully loaded.
                const tenantId = data.tenantId;
                const tenantState = state.byTenant[tenantId];
                if (tenantState?.pagination.tag === 'finished') {
                    commitForTenant({
                        tenantId,
                        data: {
                            ...tenantState,
                            data: [...(tenantState.data ?? []), res.cgResult],
                        },
                    });
                }
            },
        });
    }, 'createProvider');
    /** Updates an LTI provider on the server and updates it in the local state. */
    LTIProvidersStore.patchLtiProvider = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param _ The module context (unused).
     * @param root1 The action payload.
     * @returns A promise that resolves with the API response.
     */
    async (_, { ltiProviderId, data, }) => {
        const res = await client.p.lti.patchProvider({
            ltiProviderId,
            requestBody: data,
        });
        return res.addCallbacks({
            onSuccess: () => {
                commitSingleProvider({ provider: res.cgResult });
            },
        });
    }, 'patchLtiProvider');
    // Reset the store when the user logs out to prevent stale data.
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        LTIProvidersStore.commitClearAll();
    });
})(LTIProvidersStore || (LTIProvidersStore = {}));
